import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../contexts/AuthContext';
import './JDUploadModal.css';

const JDUploadModal = ({ isOpen, onClose, onUploadSuccess }) => {
  const { t } = useTranslation();
  const { uploadJDText, uploadJDFile } = useAuth();
  const [inputMode, setInputMode] = useState('text'); // 'text' or 'file'
  const [formData, setFormData] = useState({
    jobTitle: '',
    companyName: '',
    jdContent: ''
  });
  const [file, setFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);

  const handleInputChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
    setError(null);
  };

  const handleFileChange = (e) => {
    const selectedFile = e.target.files[0];
    setError(null);
    
    if (selectedFile) {
      // Validate file type
      if (selectedFile.type !== 'application/pdf') {
        setError(t('jdModal.errors.pdfOnly'));
        return;
      }
      
      // Validate file size (5MB limit for JDs)
      if (selectedFile.size > 5 * 1024 * 1024) {
        setError(t('jdModal.errors.fileSize'));
        return;
      }
      
      setFile(selectedFile);
    }
  };

  const validateForm = () => {
    if (!formData.jobTitle.trim()) {
      setError(t('jdModal.errors.jobTitleRequired'));
      return false;
    }
    
    if (inputMode === 'text' && !formData.jdContent.trim()) {
      setError(t('jdModal.errors.contentRequired'));
      return false;
    } else if (inputMode === 'file' && !file) {
      setError(t('jdModal.errors.fileRequired'));
      return false;
    }
    
    return true;
  };

  const handleUpload = async () => {
    if (!validateForm()) return;

    setUploading(true);
    setError(null);

    try {
      let result;
      
      if (inputMode === 'text') {
        // Upload as text
        result = await uploadJDText(
          formData.jobTitle,
          formData.companyName || '',
          formData.jdContent
        );
      } else {
        // Upload as file
        result = await uploadJDFile(
          formData.jobTitle,
          formData.companyName || '',
          file
        );
      }

      if (result.processing_status === 'completed') {
        setSuccess(true);
        setTimeout(() => {
          onUploadSuccess(result);
        }, 1500);
      } else if (result.processing_status === 'failed') {
        setError(t('jdModal.errors.uploadFailed'));
      } else {
        // Still processing
        setSuccess(true);
        setTimeout(() => {
          onUploadSuccess(result);
        }, 1500);
      }

    } catch (error) {
      setError(error.message);
    } finally {
      setUploading(false);
    }
  };

  const handleClose = () => {
    if (!uploading) {
      setFormData({ jobTitle: '', companyName: '', jdContent: '' });
      setFile(null);
      setError(null);
      setSuccess(false);
      setInputMode('text');
      onClose();
    }
  };

  if (!isOpen) return null;

  return (
    <div className={`jd-upload-modal ${isOpen ? 'open' : ''}`}>
      <div className="modal-content jd-modal">
        <div className="modal-header">
          <h3>{t('jdModal.title')}</h3>
          <button 
            className="close-btn" 
            onClick={onClose}
            aria-label={t('common.close')}
          >
            &times;
          </button>
        </div>
        
        <div className="modal-body">
          {/* Tabs for input mode */}
          <div className="input-mode-tabs">
            <button 
              className={`tab-button ${inputMode === 'text' ? 'active' : ''}`}
              onClick={() => setInputMode('text')}
              type="button"
            >
              {t('jdModal.tabs.enterText')}
            </button>
            <button 
              className={`tab-button ${inputMode === 'file' ? 'active' : ''}`}
              onClick={() => setInputMode('file')}
              type="button"
            >
              {t('jdModal.tabs.uploadFile')}
            </button>
          </div>
          
          {/* Error message */}
          {error && <div className="error-message">{error}</div>}
          
          {/* Success message */}
          {success && (
            <div className="success-message">
              <p>{t('jdModal.success.message')}</p>
              <p>{t('jdModal.success.processing')}</p>
            </div>
          )}
          
          {!success && (
            <>
              {/* Job Title */}
              <div className="form-group">
                <label htmlFor="jobTitle">
                  {t('jdModal.labels.jobTitle')} <span className="required">*</span>
                </label>
                <input
                  type="text"
                  id="jobTitle"
                  name="jobTitle"
                  value={formData.jobTitle}
                  onChange={handleInputChange}
                  placeholder={t('jdModal.placeholders.jobTitle')}
                  disabled={uploading}
                  aria-required="true"
                />
              </div>
              
              {/* Company Name */}
              <div className="form-group">
                <label htmlFor="companyName">
                  {t('jdModal.labels.companyName')}
                </label>
                <input
                  type="text"
                  id="companyName"
                  name="companyName"
                  value={formData.companyName}
                  onChange={handleInputChange}
                  placeholder={t('jdModal.placeholders.companyName')}
                  disabled={uploading}
                />
              </div>
              
              {/* Content based on input mode */}
              {inputMode === 'text' ? (
                <div className="form-group">
                  <label htmlFor="jdContent">
                    {t('jdModal.labels.jobDescription')} <span className="required">*</span>
                  </label>
                  <textarea
                    id="jdContent"
                    name="jdContent"
                    value={formData.jdContent}
                    onChange={handleInputChange}
                    placeholder={t('jdModal.placeholders.jobDescription')}
                    rows={8}
                    disabled={uploading}
                    aria-required="true"
                  />
                </div>
              ) : (
                <div className="file-upload-container">
                  <label className="file-upload-label">
                    <div className="file-upload-box">
                      <p>{t('jdModal.fileUpload.instructions')}</p>
                      <p className="file-info">
                        {file 
                          ? `${t('jdModal.fileUpload.selectedFile')}: ${file.name}` 
                          : t('common.noFileSelected')
                        }
                      </p>
                      <p className="file-requirements">
                        {t('jdModal.fileUpload.maxSize')} • {t('jdModal.fileUpload.fileType')}
                      </p>
                    </div>
                    <input
                      type="file"
                      accept=".pdf"
                      onChange={handleFileChange}
                      disabled={uploading}
                      style={{ display: 'none' }}
                    />
                  </label>
                </div>
              )}
              
              <div className="upload-requirements">
                <h4>{t('jdModal.uploadRequirements.title')}</h4>
                <ul>
                  <li>{t('jdModal.uploadRequirements.jobTitle')}</li>
                  <li>{t('jdModal.uploadRequirements.jobDescription')}</li>
                  <li>{t('jdModal.uploadRequirements.pdfOnly')}</li>
                  <li>{t('jdModal.uploadRequirements.fileSize')}</li>
                  <li>{t('jdModal.uploadRequirements.aiAnalysis')}</li>
                </ul>
              </div>
              
              <div className="modal-actions">
                <button 
                  type="button"
                  className="btn btn-secondary"
                  onClick={handleClose}
                  disabled={uploading}
                >
                  {t('common.cancel')}
                </button>
                <button
                  type="button"
                  className="btn btn-primary"
                  onClick={handleUpload}
                  disabled={uploading || !formData.jobTitle.trim() || 
                           (inputMode === 'text' && !formData.jdContent.trim()) ||
                           (inputMode === 'file' && !file)}
                >
                  {uploading ? t('common.uploading') : t('jdModal.buttons.upload')}
                </button>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default JDUploadModal;